/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useState } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import { Button, Link, Stack, Typography } from "@mui/material";
import { apiPost } from "@/src/services/apiClient";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { getCustomerConsent } from "@/src/services/customerService";
import { convertToLocaleString } from "@/src/services/dateConverter";
import RecordingConsentPrivacyModal from "./RecordingConsentPrivacyModal";
import { gaEvent } from "@/src/services/googleAnalyticsService";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

type RecordingConsentFormProps = {
  handleConsent: (state: string) => void;
};

export default function RecordingConsentForm({ handleConsent }: RecordingConsentFormProps) {
  const { customer, refreshCustomer } = useCustomer();
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!customer) {
    return null;
  }

  const consent = getCustomerConsent(customer, "callRecordingAndSummary");

  const setConsent = async (state: string) => {
    setIsLoading(true);

    gaEvent({ category: "Callaufzeichnung", action: state === "approve" ? "Zugestimmt" : "Abgelehnt" });

    try {
      await apiPost(`/customer_consents/callRecordingAndSummary/${state}`, {});

      refreshCustomer();

      handleConsent(state);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setIsPrivacyModalOpen(true);
  };

  return (
    <FormFader>
      <ContentBox headline={"Unser Service für Sie: Persönliche Zusammenfassung"}>
        <Typography variant={"body2"}>
          <strong>Sie erhalten bei uns vertraulich und kostenlos eine Zusammenfassung Ihres Gesprächs.</strong> So müssen Sie nicht mitschreiben, sondern können
          sich voll und ganz auf das Gespräch konzentrieren. Wenn Sie dies möchten, können wir hierzu <u>alle zukünftigen Beratungsgespräche</u> bei uns
          aufzeichnen und fassen die Kernthemen, Absprachen und das weitere Vorgehen für Sie zusammen. So haben Sie alle Informationen jederzeit griffbereit!
        </Typography>
        <Typography variant={"body2"} sx={{ marginTop: "1rem" }}>
          <strong>
            Weitere Informationen hierzu finden Sie unter{" "}
            <Link onClick={openModal} sx={{ textDecoration: "none", cursor: "pointer" }}>
              Datenschutzhinweise zur Gesprächsaufzeichnung.
            </Link>
          </strong>
        </Typography>
        {consent && (
          <Typography variant={"body2"} sx={{ marginTop: "1rem" }}>
            <strong>Status: </strong>
            {consent.approvalDate && <>Zugestimmt am {convertToLocaleString(consent.approvalDate)}</>}
            {consent.denialDate && <>Abgelehnt am {convertToLocaleString(consent.denialDate)}</>}
          </Typography>
        )}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ marginTop: "1.5rem", marginBottom: "1.5rem" }}>
          <Button fullWidth size={"small"} variant={"contained"} onClick={() => setConsent("approve")} disabled={isLoading}>
            Zustimmen
          </Button>

          <Button fullWidth size={"small"} variant={"outlined"} onClick={() => setConsent("deny")} disabled={isLoading}>
            Ablehnen
          </Button>
        </Stack>
        <Typography variant={"body2"}>
          Sie haben die Möglichkeit, Ihre Einwilligung <strong>jederzeit</strong> in Ihrer Servicewelt zu widerrufen.
        </Typography>
        <RecordingConsentPrivacyModal open={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
      </ContentBox>
    </FormFader>
  );
}
